import i18next from 'i18next';
import {
  accessScopeType,
  formatError,
  isErrorCode,
  permission,
  systemRoleKey,
  type ErrorParams,
  type IsoDate,
} from '@overseer/contracts';
import { PERMISSION_LABEL, ROLE_LABEL, SCOPE_TYPE_LABEL } from '@/features/admin/labels';
import { currentLocale } from '../i18n';
import { formatDate } from '../time';

/**
 * A coded API error in the person's language.
 *
 * The server sends a code and its values; the text for each language lives in
 * `ERRORS` in the contracts. The server's English `message` is the fallback for
 * a code this build does not know: an API deployed ahead of the web.
 */
export const errorText = (code: string | undefined, params: ErrorParams | undefined, fallback: string): string => {
  if (!code) return fallback;

  const key = textKey(code);
  if (!key) return fallback;

  return formatError(key, readable(params ?? {}), currentLocale());
};

/** An issue's code, falling back from `validation.too_small.string` to `validation.too_small`. */
const textKey = (code: string) => {
  if (isErrorCode(code)) return code;

  const parent = code.slice(0, code.lastIndexOf('.'));

  return parent.includes('.') && isErrorCode(parent) ? parent : undefined;
};

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Keys the server sends as identifiers, shown by their labels; a calendar date as the person reads one. */
const readable = (params: ErrorParams): ErrorParams => {
  const result = { ...params };
  const t = i18next.t.bind(i18next);

  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && CALENDAR_DATE.test(value)) result[key] = formatDate(value as IsoDate);
  }

  const held = permission.safeParse(params.permission);
  if (held.success) result.permission = t(PERMISSION_LABEL[held.data]);

  const scope = accessScopeType.safeParse(params.scope);
  if (scope.success) result.scope = t(SCOPE_TYPE_LABEL[scope.data]).toLocaleLowerCase();

  const role = systemRoleKey.safeParse(params.role);
  if (role.success) result.role = t(ROLE_LABEL[role.data]);
  else if (typeof params.roleName === 'string') result.role = params.roleName;

  return result;
};
